import { type AxiosError } from 'axios'
import { useMutation } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import {
  type RunTestEvaluationParams,
  type UseIntegrationInvocationRunTestEvaluationOptions,
  type UseIntegrationInvocationRunTestEvaluationReturnType,
  type RunTestEndpointResponse
} from 'queries/external-integrations/types'
import {
  getEndpointWithParams,
  throwIfEmptyResponse
} from 'queries/queries-utils'
import {
  throwIfBadResponseForRunTestEndpoint,
  throwIfFailedPredicateForRunTestEndpoint
} from 'queries/status-checks/query-utils'

const getEndpointByInvocationId = (invocationId: string) =>
  `integration-invocations/${invocationId}/run-test-evaluation`

function runTestEvaluation(teamId: string, signal?: AbortSignal) {
  return async function ([invocationId, requestBody]: RunTestEvaluationParams) {
    const endpoint = getEndpointByInvocationId(invocationId)
    const endpointWithParams = getEndpointWithParams(endpoint, { teamId })
    const response = await agent.post<RunTestEndpointResponse>(
      endpointWithParams,
      requestBody,
      { signal }
    )

    throwIfEmptyResponse(response)
    throwIfBadResponseForRunTestEndpoint(response)
    throwIfFailedPredicateForRunTestEndpoint(response)

    return response.data
  }
}

// Export function type signatures
export function useIntegrationInvocationRunTestEvaluation(
  signal?: AbortSignal,
  options?: UseIntegrationInvocationRunTestEvaluationOptions
): UseIntegrationInvocationRunTestEvaluationReturnType

export function useIntegrationInvocationRunTestEvaluation(
  options?: UseIntegrationInvocationRunTestEvaluationOptions
): UseIntegrationInvocationRunTestEvaluationReturnType

/**
 * Submits a POST request which validates the evaluation configuration of an Integration Invocation.
 * This is primarily used for PNI types, and requires a Webhook Invocation first be created.
 */
export function useIntegrationInvocationRunTestEvaluation(
  ...args:
    | [AbortSignal?, UseIntegrationInvocationRunTestEvaluationOptions?]
    | [UseIntegrationInvocationRunTestEvaluationOptions?]
): UseIntegrationInvocationRunTestEvaluationReturnType {
  const [signalOrOptions, options] = args
  const { currentTeamId: teamId } = useAuth()
  const hasSignal = signalOrOptions instanceof AbortSignal

  return useMutation<
    RunTestEndpointResponse,
    AxiosError,
    RunTestEvaluationParams
  >({
    ...(options ?? signalOrOptions),
    mutationFn: runTestEvaluation(
      teamId,
      hasSignal ? signalOrOptions : undefined
    )
  })
}
